import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ControlledDateInput } from '../../../../components/form/Datepicker';
import { ControlledInput } from '../../../../components/form/Input';
import { useToasts } from '../../../../context/Toast';
import { useMutationErrorHandler } from '../../../../hooks/useMutationErrorHandler';
import { useQueryParams } from '../../../../hooks/useQueryParams';
import { currentYearPeriod, toMonthRange } from '../../../../utils/date';
import { isAfter } from 'date-fns';
import * as yup from 'yup';

import {
  useEmployeeAbsencesRefetchOption,
  useMyAbsencesRefetchOption,
} from '../../pages/Absences/useMyAbsencesRefetchOption';
import {
  AbsenceType,
  AbsenceOverlappingType,
  UpsertAbsenceInput,
  useUpsertAbsenceMutation,
  useValidateRequestAbsenceMutation,
} from '../../__generated__/graphql';
import { ModalSidebarFormContent } from '../ModalSidebar';
import { AbsenceFileField, FileInput } from './AbsenceFileField';
import { AbsenceFormProps } from './AbsenceFormProps';
import FormBody from './FormBody';
import FormButtons from './FormButtons';
import { getOverlappingMessage, isDocumentUploadVisible } from './helper';
import OverlappingAbsences from './OverlappingAbsences';
import RequestingAmountResult from './RequestingAmountResult';
import SpecialRulesCard from './SpecialRulesCard';
import { RecordedLoomVideo } from '../LoomVideo';
import { Maybe } from '../../../../utils/maybe';
import { useUserInfo } from '../../context/UserInfo';
import { useEmployeeIdContext } from '../../context/EmployeeIdContext';
import { ExtraFieldsArea, ExtraFieldsSupportedObject } from '../ExtraFields';
import { collectFormDataExtraFields } from '../ExtraFields/logic';
import {
  AbsenceFormContext,
  useAbsenceFieldsForm,
} from './useAbsenceFieldsForm';
import { ExtraFieldsFormId } from '../ExtraFields/types';
import { AbsenceDefaultValues } from '../EditAbsenceSidebar/types';
import { useExtraAbsenceExtraFieldsContextParams } from './useExtraAbsenceExtraFieldsContextParams';
import { mapToExtraFieldInput } from '../ExtraFields/extraFieldInternalValue';

export type HalfDay = 'true' | 'false';

export type FormData = {
  startDate: Date;
  startDateHalfDay: HalfDay;
  endDate: Date;
  endDateHalfDay: HalfDay;
  note?: string;
  loomVideo?: Maybe<RecordedLoomVideo>;
};

const absenceFormContext: AbsenceFormContext = {
  requireNote: false,
};

const getDefaultValues = (defaultValues: Maybe<AbsenceDefaultValues>) => {
  if (!defaultValues) {
    return {
      startDateHalfDay: 'false' as HalfDay,
      endDateHalfDay: 'false' as HalfDay,
    };
  }

  return {
    startDate: defaultValues.startDate
      ? new Date(defaultValues.startDate)
      : undefined,
    startDateHalfDay: defaultValues.startDateHalfDay
      ? 'true'
      : ('false' as HalfDay),
    endDate: defaultValues.endDate
      ? new Date(defaultValues.endDate)
      : undefined,
    endDateHalfDay: defaultValues.endDateHalfDay
      ? 'true'
      : ('false' as HalfDay),
    note: defaultValues.note,
  };
};

const mapFormToInput = (
  formData: FormData & {
    attachedFile?: FileInput | null;
  } & Pick<
      AbsenceFormProps,
      | 'categoryId'
      | 'policyId'
      | 'absenceId'
      | 'employeeId'
      | 'documentCategoryId'
    >,
): UpsertAbsenceInput => {
  const {
    categoryId,
    policyId,
    absenceId,
    startDate,
    startDateHalfDay,
    endDate,
    endDateHalfDay,
    note,
    attachedFile,
    loomVideo,
    employeeId,
    documentCategoryId,
  } = formData;

  return {
    id: absenceId ?? null,
    employeeId: employeeId ?? null,
    flair__Employee_Absence_Category__c: categoryId,
    flair__Employee_Absence_Request_Policy__c: policyId,
    flair__Start_Date__c: moment(startDate).format('YYYY-MM-DD'),
    flair__Start_Time__c: startDateHalfDayToTime(startDateHalfDay),
    flair__End_Date__c: moment(endDate).format('YYYY-MM-DD'),
    flair__End_Time__c: endDateHalfDayToTime(endDateHalfDay),
    flair__Comment__c: note ?? null,
    attachedFile: attachedFile ?? null,
    loomVideo: loomVideo ?? null,
    extraFields: collectFormDataExtraFields(formData).map(mapToExtraFieldInput),
    daysTimes: null,
    documentCategoryId: documentCategoryId ?? null,
  };
};

const HALF_DAY_TIME = '12:00:00';

const startDateHalfDayToTime = (halfDay: HalfDay): string =>
  halfDay === 'true' ? HALF_DAY_TIME : '00:00:00';

const endDateHalfDayToTime = (halfDay: HalfDay): string =>
  halfDay === 'true' ? HALF_DAY_TIME : '23:59:59';

type Props = AbsenceFormProps & {
  validationSchema: yup.ObjectSchema<FormData>;
  absenceId?: Maybe<string>;
};

const DailyAbsenceForm: React.FC<React.PropsWithChildren<Props>> = ({
  categoryId,
  categoryName,
  policyId,
  unlimited,
  withoutBalance,
  onClose,
  specialRules,
  absenceCategoriesGroupSelect,
  absenceCategorySelect,
  accrualPolicyInterval,
  absenceRequestPolicySelect,
  absenceRequestPolicyRules,
  requireDocumentUpload,
  minAmountForDocumentUpload,
  documentCategoryId,
  requireNote,
  notePlaceholder,
  onSuccess,
  refetchQueries,
  validationSchema,
  defaultValues,
  absenceId,
}) => {
  const { t } = useTranslation();
  const { addSuccess } = useToasts();
  const { id: meId } = useUserInfo();
  const { employeeId, isManagerView } = useEmployeeIdContext();
  const isEditingMode = !!absenceId;
  const { month, year: yearStr } = useQueryParams();
  const period = month ? toMonthRange(month) : undefined;
  const year = yearStr ? parseInt(yearStr) : undefined;
  const yearPeriod = Number.isInteger(year)
    ? currentYearPeriod(year)
    : undefined;
  const myAbsencesRefetchQuery = useMyAbsencesRefetchOption(period);
  const employeeAbsencesRefetchQuery = useEmployeeAbsencesRefetchOption(
    meId,
    yearPeriod,
  );
  const [upsertAbsence] = useUpsertAbsenceMutation({
    awaitRefetchQueries: true,
    /* eslint-disable-next-line no-restricted-syntax */
    refetchQueries: [
      ...(isManagerView
        ? []
        : [myAbsencesRefetchQuery, ...employeeAbsencesRefetchQuery]),
      ...(refetchQueries ?? []),
    ],
  });

  const errorHandler = useMutationErrorHandler();

  absenceFormContext.requireNote = requireNote;

  const contextParams = useExtraAbsenceExtraFieldsContextParams({
    employeeAbsenceCategoryId: categoryId,
    categoryName,
  });

  const initialValues = getDefaultValues(defaultValues);

  const form = useAbsenceFieldsForm<FormData>({
    defaultValues: initialValues,
    contextParams,
    absenceFormContext,
    validationSchema,
  });

  const { watch } = form;
  const { startDate, startDateHalfDay, endDate, endDateHalfDay } = watch();

  const [absenceFile, setAbsenceFile] = useState<Maybe<FileInput>>(
    defaultValues?.attachedFile,
  );
  const [
    validateRequestAbsence,
    { loading: validating, data: validateRequestAbsenceData },
  ] = useValidateRequestAbsenceMutation();

  useEffect(() => {
    if (
      categoryId &&
      startDate &&
      startDateHalfDay &&
      endDate &&
      endDateHalfDay &&
      !isAfter(startDate, endDate)
    ) {
      const { documentCategoryId: _documentCategoryId, ...validationInput } =
        mapFormToInput({
          categoryId,
          policyId,
          startDate,
          startDateHalfDay,
          endDate,
          endDateHalfDay,
          absenceId,
          employeeId: employeeId,
          documentCategoryId,
        });
      validateRequestAbsence({
        variables: {
          input: validationInput,
        },
      });
    }
  }, [
    validateRequestAbsence,
    categoryId,
    policyId,
    startDate,
    startDateHalfDay,
    endDate,
    endDateHalfDay,
    absenceId,
    employeeId,
    meId,
    documentCategoryId,
  ]);

  const validationResult =
    validateRequestAbsenceData?.absence.validateAbsenceRequest;

  const documentUploadVisible = isDocumentUploadVisible(
    requireDocumentUpload,
    minAmountForDocumentUpload,
    validationResult?.requestedAmount,
  );

  const overlappingError =
    validationResult?.error?.__typename ===
    'ValidateAbsenceRequestOverlappingAbsencesError'
      ? validationResult.error
      : null;

  const overlappingAbsences = overlappingError?.overlappingAbsences ?? [];
  const overlappingType = overlappingError?.overlappingType;
  const hasOverlappingAbsences = overlappingAbsences?.length > 0;

  // Block an overlapping-absence error unless the category explicitly permits
  // overlap. Fail safe: a missing/unknown type still blocks, and we don't rely
  // on the overlappingAbsences list being non-empty.
  const hasOverlappingError =
    !!overlappingError &&
    overlappingType !== AbsenceOverlappingType.Allowed &&
    overlappingType !== AbsenceOverlappingType.AllowedWithOverride;
  const hasOtherError =
    !!validationResult?.error &&
    validationResult.error.__typename !==
      'ValidateAbsenceRequestOverlappingAbsencesError';
  const hasError = hasOverlappingError || hasOtherError;
  const hasInvalidFile = !!(documentUploadVisible && !absenceFile);

  const overlappingMessage = getOverlappingMessage(
    t,
    overlappingType,
    hasOverlappingAbsences,
  );

  const error = hasOverlappingError ? overlappingMessage : undefined;

  const onSubmit = useCallback(
    async (values: FormData) => {
      // block submit action
      if (hasError) {
        return;
      }

      await upsertAbsence({
        variables: {
          input: mapFormToInput({
            ...values,
            categoryId,
            policyId,
            attachedFile: absenceFile,
            absenceId: absenceId,
            employeeId: employeeId,
            documentCategoryId,
          }),
        },
      })
        .then(() => {
          addSuccess(t('requestAbsence.successMessage'));
          onSuccess && onSuccess();
          onClose();
        })
        .catch(errorHandler);
    },
    [
      hasError,
      upsertAbsence,
      categoryId,
      policyId,
      absenceFile,
      errorHandler,
      addSuccess,
      t,
      onSuccess,
      onClose,
      absenceId,
      employeeId,
      documentCategoryId,
    ],
  );

  return (
    <ModalSidebarFormContent
      form={form}
      onSubmit={onSubmit}
      body={
        <FormBody
          absenceCategoriesGroupSelect={absenceCategoriesGroupSelect}
          absenceCategorySelect={absenceCategorySelect}
          absenceRequestPolicySelect={absenceRequestPolicySelect}
          absenceRequestPolicyRules={absenceRequestPolicyRules}
          specialRules={
            specialRules ? (
              <SpecialRulesCard richText={specialRules} />
            ) : undefined
          }
          startDateInput={
            <ControlledDateInput name="startDate" disabled={isEditingMode} />
          }
          startDateTimeInput={
            <ControlledInput
              type="select"
              name="startDateHalfDay"
              disabled={isEditingMode}
              readOnly={isEditingMode}>
              <option value="false">
                {t('requestAbsence.form.halfDayOptions.morning')}
              </option>
              <option value="true">
                {t('requestAbsence.form.halfDayOptions.lunch')}
              </option>
            </ControlledInput>
          }
          endDateInput={
            <ControlledDateInput
              name="endDate"
              options={{
                minDate: startDate,
              }}
              disabled={isEditingMode}
            />
          }
          endDateTimeInput={
            <ControlledInput
              type="select"
              name="endDateHalfDay"
              disabled={isEditingMode}
              readOnly={isEditingMode}>
              <option value="true">
                {t('requestAbsence.form.halfDayOptions.lunch')}
              </option>
              <option value="false">
                {t('requestAbsence.form.halfDayOptions.evening')}
              </option>
            </ControlledInput>
          }
          fileInput={
            documentUploadVisible ? (
              <AbsenceFileField
                file={absenceFile}
                saving={validating}
                onSelectFile={setAbsenceFile}
              />
            ) : null
          }
          requireNote={requireNote}
          noteInput={
            <ControlledInput
              name="note"
              type="textarea"
              placeholder={notePlaceholder ?? ''}
              disabled={isEditingMode}
            />
          }
          requestingAmountBadge={
            <RequestingAmountResult
              data={validationResult}
              loading={validating}
              type={AbsenceType.Daily}
              accrualPolicyInterval={accrualPolicyInterval}
              unlimited={unlimited}
              withoutBalance={withoutBalance}
              absenceStartDate={startDate}
            />
          }
          //TODO:: Temp solution: The whole section is hidden now, later we can work on it.
          extraFields={
            isEditingMode ? null : (
              <ExtraFieldsArea
                objectApiName={ExtraFieldsSupportedObject.Absence}
                formId={ExtraFieldsFormId.AbsencesRequestAbsence}
                contextParams={contextParams}
                disabled={isEditingMode}
              />
            )
          }
          overlappingAbsences={
            hasOverlappingAbsences ? (
              <OverlappingAbsences
                absences={overlappingAbsences}
                loading={validating}
                message={overlappingMessage}
              />
            ) : undefined
          }
          isEditingMode={isEditingMode}
        />
      }
      footer={
        <FormButtons
          disabled={hasError || validating || hasInvalidFile}
          error={error}
          isEditing={!!absenceId}
        />
      }
    />
  );
};

export default DailyAbsenceForm;
